import { useEffect, useState } from "react";
import PageCTA from "@/components/PageCTA";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import VideoCard from "@/components/VideoCard";
import FeaturedFlights from "@/components/FeaturedFlights";
import { Reveal, MaskedLine, HighlandBackdrop } from "@/components/Reveal";
import { pickWeeklyFeatured, isoWeekLabel } from "@/lib/weeklyRotation";
import { Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const REGIONS = ["All", "Highlands", "Islands", "Cities"];

export default function Gallery() {
    const [params, setParams] = useSearchParams();
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const region = params.get("region") || "All";
    const navigate = useNavigate();
    const { user } = useAuth();

    useEffect(() => {
        setLoading(true);
        const q = region === "All" ? {} : { region };
        api.get("/videos", { params: q })
            .then((r) => setVideos(r.data))
            .finally(() => setLoading(false));
    }, [region]);

    const setRegion = (r) => {
        if (r === "All") setParams({});
        else setParams({ region: r });
    };

    const handleLock = (video) => {
        if (!user) navigate("/register?next=/gallery");
        else if (video?.lock_reason === "subscription_required") navigate("/subscription");
        else navigate("/subscription");
    };

    return (
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="gallery-page">
            <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10 mb-16 relative">
                <div className="max-w-3xl">
                    <Reveal>
                        <div className="overline mb-4"><span className="editorial-rule" />Gallery</div>
                    </Reveal>
                    <h1
                        className="font-serif font-light tracking-tighter leading-[0.9]"
                        style={{ fontSize: "clamp(2.8rem, 7vw, 7rem)" }}
                        data-testid="gallery-title"
                    >
                        <div><MaskedLine delay={0.1}>The flight</MaskedLine></div>
                        <div>
                            <MaskedLine delay={0.28}>
                                <span className="italic text-[var(--sb-brand)]">log.</span>
                            </MaskedLine>
                        </div>
                    </h1>
                    <Reveal delay={0.4}>
                        <p className="text-[var(--sb-text-dim)] mt-8 max-w-xl text-lg leading-relaxed">
                            Filter by region or watch the lot. Locked clips are part of the £1.89 subscriber drop.
                        </p>
                    </Reveal>
                </div>
                <Reveal delay={0.35}>
                    <div className="flex flex-wrap gap-2" data-testid="region-filter">
                        {REGIONS.map((r) => (
                            <button
                                key={r}
                                onClick={() => setRegion(r)}
                                className={`px-5 py-2 text-xs tracking-[0.22em] uppercase border transition-colors ${
                                    region === r
                                        ? "bg-[var(--sb-brand)] text-[var(--sb-bg)] border-[var(--sb-brand)]"
                                        : "border-[var(--sb-border)] text-[var(--sb-text-dim)] hover:border-[var(--sb-brand)] hover:text-[var(--sb-text)]"
                                }`}
                                data-testid={`region-filter-${r.toLowerCase()}`}
                            >
                                {r}
                            </button>
                        ))}
                    </div>
                </Reveal>
            </header>

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="aspect-video bg-[var(--sb-surface)] border border-[var(--sb-border)] animate-pulse" />
                    ))}
                </div>
            ) : (() => {
                // Rotating carousel: all `featured` videos become slides.
                // The pool starts at this week's canonical pick so every visitor lands on the same
                // opening frame, then advances automatically every 7 seconds.
                const pool = region === "All" ? videos.filter((v) => v.featured) : [];
                const startWith = pool.length > 0 ? pickWeeklyFeatured(pool) : null;
                const ordered = startWith
                    ? [startWith, ...pool.filter((v) => v.id !== startWith.id)]
                    : [];
                const gridVideos = ordered.length > 0 ? videos.filter((v) => !ordered.find((o) => o.id === v.id)) : videos;
                return (
                    <>
                        {ordered.length > 0 && (
                            <FeaturedFlights
                                videos={ordered}
                                weekLabel={ordered.length > 1 ? isoWeekLabel() : ""}
                                onLockClick={(v) => handleLock(v)}
                            />
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8" data-testid="gallery-grid">
                            {gridVideos.map((v) => (
                                <VideoCard key={v.id} video={v} allVideos={videos} onLockClick={() => handleLock(v)} />
                            ))}
                        </div>
                    </>
                );
            })()}

            {!user?.is_subscribed && videos.some((v) => v.exclusive) && (
                <div className="mt-14 card p-8 lg:p-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6" data-testid="gallery-subscribe-promo">
                    <div className="flex items-start gap-4">
                        <Lock className="text-[var(--sb-brand)] mt-1" />
                        <div>
                            <h3 className="font-serif text-2xl mb-1">Unlock the exclusive cuts</h3>
                            <p className="text-[var(--sb-text-dim)] text-sm max-w-md">Behind-the-scenes reels, RAW LOG downloads, and the films I&rsquo;m too superstitious to publish publicly.</p>
                        </div>
                    </div>
                    <Link to="/subscription" className="btn-primary" data-testid="gallery-subscribe-button">Subscribe — £1.89</Link>
                </div>
            )}
            <PageCTA variant="book" className="!px-0" />
            <HighlandBackdrop variant="soft" />
        </div>
    );
}
